import React, { useState } from 'react';
import { useStore, uid } from '../state/store';
import { Card, Empty, SectionHead, Text } from './ui';
import EvidenceForm from './EvidenceForm';
import { ActionForm } from './ActionForm';
import { fmtDate } from '../lib/dates';
import { closeAsWaitingExternal, commercialCollectionPath } from '../lib/actions';
import { OUTCOME_OPTIONS, evidenceCandidateFromOutcome } from '../lib/outcomes';

function expectedResultCopy(t) {
  if (t?.expectedResult) return `Expected result: ${t.expectedResult}`;
  const level = t?.expectedEvidence;
  if (!level || level === 'OPS' || level === 'none' || level === 'E0') {
    return 'Expected result: preparation complete — no commercial proof.';
  }
  if (level === 'E1') return 'Expected result: a reply, introduction, or meeting booked.';
  if (level === 'E2') return 'Expected result: a confirmed customer problem.';
  if (level === 'E3') return 'Expected result: a named asset or commercial commitment of information.';
  if (level === 'E4') return 'Expected result: a commercial commitment.';
  if (level === 'E5') return 'Expected result: binding proof.';
  return 'Expected result: record what happened; Bridge will determine what it proves.';
}

function isBridgeCreated(t) {
  return t?.source === 'Bridge' || !!t?.bridgeCreated || !!t?.preparation;
}

export function TodayCommitted({ go, compact = false, launchPrep = null }) {
  const { state, d, patch, add, today } = useStore();
  const { top3 } = d;
  const [modal, setModal] = useState(null);
  const [closeId, setCloseId] = useState(null);
  const [outcomeId, setOutcomeId] = useState('conversation');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [disposition, setDisposition] = useState('evidence');
  const [waitingUntil, setWaitingUntil] = useState('');
  const [waitingFor, setWaitingFor] = useState('');
  const aiPlan = [...(state.top3 || [])]
    .filter((t) => Number(t.aiPlanRank) > 0 && t.status !== 'Closed' && t.status !== 'Parked' && t.status !== 'Superseded')
    .sort((a, b) => Number(a.aiPlanRank) - Number(b.aiPlanRank));
  const committed = (top3.today || []).filter(
    (t) => (t.selectedForToday || t.status === 'Today') && t.source === 'Manual' && !Number(t.aiPlanRank),
  );

  const closeWithOutcome = (t) => {
    const prep = isBridgeCreated(t) || t.expectedEvidence === 'OPS' || t.evidenceCredit === 'none';
    if (!prep) {
      const { record, validation } = evidenceCandidateFromOutcome({
        outcomeId,
        action: t,
        today,
        note: outcomeNote,
      });
      add('evidence', { ...record, id: uid('ev') });
      if (disposition === 'commercial' && t.linkedId && commercialCollectionPath(t.linkedKind)) {
        const result = closeAsWaitingExternal(t, {
          waitingUntil,
          waitingFor: waitingFor || t.linkedLabel || t.text,
          today,
        });
        patch('top3', t.id, {
          ...result.moveChanges,
          completionResult: outcomeId,
          outcome: outcomeId,
          outcomeNote,
          evidenceCreated: validation.level,
        });
        patch(commercialCollectionPath(t.linkedKind), t.linkedId, result.commercialChanges);
        add('top3', { ...result.followUp, id: uid('t3') });
        setCloseId(null);
        setOutcomeNote('');
        setWaitingUntil('');
        setWaitingFor('');
        return;
      }
      patch('top3', t.id, {
        status: 'Closed',
        disposition: outcomeId === 'no_response' ? 'stopped' : 'evidence',
        completionResult: outcomeId,
        outcome: outcomeId,
        outcomeNote,
        evidenceCreated: validation.level,
        completedAt: today,
        closedAt: today,
        selectedForToday: false,
        todayRank: 0,
        aiPlanRank: 0,
      });
    } else {
      // Preparation / Bridge-created: close without commercial evidence or commitment.
      patch('top3', t.id, {
        status: 'Closed',
        disposition: 'learning',
        completionResult: 'operational',
        outcome: 'operational',
        outcomeNote: outcomeNote || 'Preparation complete — not commercial proof.',
        evidenceCreated: '',
        completedAt: today,
        closedAt: today,
        selectedForToday: false,
        todayRank: 0,
        aiPlanRank: 0,
      });
    }
    setCloseId(null);
    setOutcomeNote('');
    setWaitingUntil('');
    setWaitingFor('');
  };

  const moves = [...aiPlan, ...committed];
  const executionBegins = launchPrep?.executionBegins || d.sprint?.start;

  return (
    <section className="section">
      <SectionHead
        title={compact ? 'Today' : "Today's Plan"}
        term="founderCommitment"
        hint={compact ? undefined : 'After completing a move, record what happened; Bridge will determine what it proves.'}
        right={
          <div className="row tight">
            <button
              className="btn sm"
              onClick={() => setModal({ isNew: true, selectedForToday: true, status: 'Today' })}
            >
              {compact ? 'Add' : 'Add move'}
            </button>
          </div>
        }
      />

      <Card flush>
        {moves.length === 0 && (
          <div style={{ padding: 14 }}>
            {launchPrep?.readyForLaunch ? (
              <Empty title="Ready for launch — no founder action required today.">
                Execution begins {executionBegins ? fmtDate(executionBegins) : 'on sprint start'}.
              </Empty>
            ) : (
              <Empty title={(state.accounts || []).length === 0 ? 'Import Dubai accounts first.' : 'No founder move for today yet.'}>
                {(state.accounts || []).length === 0
                  ? 'Bridge will propose a provisional plan after import.'
                  : 'Bridge will add a preparation or outreach move when the recorded state calls for one. You can add a move anytime.'}
              </Empty>
            )}
          </div>
        )}

        {moves.map((t, i) => (
          <MoveRow
            key={t.id}
            t={t}
            i={i}
            compact={compact}
            closeId={closeId}
            setCloseId={setCloseId}
            setModal={setModal}
            patch={patch}
            outcomeId={outcomeId}
            setOutcomeId={setOutcomeId}
            outcomeNote={outcomeNote}
            setOutcomeNote={setOutcomeNote}
            disposition={disposition}
            setDisposition={setDisposition}
            waitingUntil={waitingUntil}
            setWaitingUntil={setWaitingUntil}
            waitingFor={waitingFor}
            setWaitingFor={setWaitingFor}
            onConfirm={() => closeWithOutcome(t)}
            prep={isBridgeCreated(t)}
          />
        ))}
      </Card>

      {modal?.recordEvidence && (
        <EvidenceForm
          defaults={{ linkedTo: modal.linkedLabel || modal.text, market: modal.workstream === 'Saudi' ? 'Saudi' : 'Dubai' }}
          onClose={() => setModal(null)}
        />
      )}
      {modal && !modal.recordEvidence && <ActionForm draft={modal} onClose={() => setModal(null)} />}
    </section>
  );
}

function MoveRow({
  t, i, compact, closeId, setCloseId, setModal, patch,
  outcomeId, setOutcomeId, outcomeNote, setOutcomeNote,
  disposition, setDisposition, waitingUntil, setWaitingUntil, waitingFor, setWaitingFor,
  onConfirm, prep,
}) {
  return (
    <div className={`top3-item ${t.status === 'Closed' || t.status === 'Completed' ? 'done' : ''}`}>
      <span className="n">{t.aiPlanRank || i + 1}</span>
      <div style={{ flex: 1 }}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          {compact ? (
            <button type="button" className="t today-move-hit" onClick={() => setModal(t)}>{t.text}</button>
          ) : (
            <div className="t" style={{ flex: 1 }}>
              {t.text}
              {prep ? (
                <span className="chip" style={{ marginLeft: 8, fontSize: 11 }}>Bridge-created</span>
              ) : null}
            </div>
          )}
          {!compact && (
            <div className="row tight">
              <button className="btn sm ghost" onClick={() => setModal(t)}>Change</button>
              <button
                className="btn sm ghost"
                onClick={() => patch('top3', t.id, { selectedForToday: false, todayRank: 0, aiPlanRank: 0, status: 'Ready' })}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
        {t.whyNow ? <div className="why"><strong>Why now.</strong> {t.whyNow}</div> : null}
        <div className="exp">
          <span className="dim">{expectedResultCopy(t)}</span>
          {t.linkedLabel && <span className="dim"> · {t.linkedLabel}</span>}
          {t.dueDate && <span className="dim"> · due {fmtDate(t.dueDate)}</span>}
        </div>
        <div className="row tight" style={{ marginTop: 9 }}>
          <button className="btn sm" onClick={() => { setCloseId(t.id); setOutcomeId('conversation'); setDisposition('evidence'); }}>
            {prep ? 'Mark preparation done' : 'Record outcome'}
          </button>
        </div>
        {closeId === t.id && (
          <div style={{ marginTop: 8 }}>
            {prep ? (
              <>
                <div className="dim tiny" style={{ marginBottom: 6 }}>
                  Preparation only — closing does not create commercial proof or a founder commitment.
                </div>
                <Text label="Optional note" value={outcomeNote} onChange={setOutcomeNote} placeholder="What was verified?" />
                <div className="row" style={{ marginTop: 8 }}>
                  <button className="btn sm primary" onClick={onConfirm}>Confirm</button>
                </div>
              </>
            ) : (
              <>
                <div className="dim tiny" style={{ marginBottom: 6 }}>What happened?</div>
                <div className="row wrap" style={{ gap: 8 }}>
                  {OUTCOME_OPTIONS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`btn sm ${outcomeId === o.id ? 'primary' : ''}`}
                      onClick={() => setOutcomeId(o.id)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <Text label="Optional note" value={outcomeNote} onChange={setOutcomeNote} placeholder="What exactly happened?" />
                {t.linkedId && commercialCollectionPath(t.linkedKind) && (
                  <>
                    <div className="dim tiny" style={{ marginTop: 8, marginBottom: 6 }}>Finish as</div>
                    <div className="row wrap" style={{ gap: 8 }}>
                      <button
                        type="button"
                        className={`btn sm ${disposition === 'evidence' ? 'primary' : ''}`}
                        onClick={() => setDisposition('evidence')}
                      >
                        Closed
                      </button>
                      <button
                        type="button"
                        className={`btn sm ${disposition === 'commercial' ? 'primary' : ''}`}
                        onClick={() => setDisposition('commercial')}
                      >
                        Waiting on counterparty
                      </button>
                    </div>
                    {disposition === 'commercial' && (
                      <div className="grid g2" style={{ marginTop: 8, maxWidth: 520 }}>
                        <Text label="Waiting until" type="date" value={waitingUntil} onChange={setWaitingUntil} />
                        <Text label="Waiting for" value={waitingFor} onChange={setWaitingFor} placeholder="Named reply, document, or decision" />
                      </div>
                    )}
                  </>
                )}
                <div className="row" style={{ marginTop: 8 }}>
                  <button className="btn sm primary" onClick={onConfirm}>Close move</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
